// src/admin/FormEditor/components/OptionEditor.jsx
// Editor for multiple choice options (radio/checkbox/dropdown)
import { GripVertical, Trash2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  Languages,
  ENABLE_EXPERIMENTAL_FEATURE,
} from "../../../constants/common";
import { removeOption, updateOption } from "../store/formEditorSlice.js";
import { createDefaultOption } from "../utils/formTemplates.js";

const OptionEditor = ({ questionIndex, optionIndex }) => {
  const dispatch = useDispatch();
  const supported_languages = useSelector(
    (state) => state.formEditor.formMetadata.supported_languages
  );
  const option = useSelector(
    (state) =>
      state.formEditor.formMetadata.questions[questionIndex].options[
        optionIndex
      ]
  );
  const question = useSelector(
    (state) => state.formEditor.formMetadata.questions[questionIndex]
  );
  const defaultOption = createDefaultOption(supported_languages);

  const handleCustomReportHeaderChange = (value) => {
    const updates = { custom_report_header: value };
    dispatch(updateOption({ questionIndex, optionIndex, updates }));
  };

  const handleDescChange = (lang, value) => {
    const updates = { desc: { ...option.desc, [lang]: value } };
    dispatch(updateOption({ questionIndex, optionIndex, updates }));
  };

  const handleExcludeMutexChange = (e) => {
    const updates = { exclude_mutex: e.target.checked };
    dispatch(updateOption({ questionIndex, optionIndex, updates }));
  };

  const handleOtherOptionChange = (e) => {
    const updates = {
      other_option: e.target.checked,
      // If enabling other_option, set default placeholders
      ...(e.target.checked && {
        other_option_placeholder: {
          en: "Please specify",
          tc: "請註明",
          sc: "请注明",
        },
      }),
    };
    dispatch(updateOption({ questionIndex, optionIndex, updates }));
  };

  const handleOtherOptionPlaceholderChange = (lang, value) => {
    const updates = {
      other_option_placeholder: {
        ...(option.other_option_placeholder || {}),
        [lang]: value,
      },
    };
    dispatch(updateOption({ questionIndex, optionIndex, updates }));
  };

  const handleRemove = () => {
    dispatch(removeOption({ questionIndex, optionIndex }));
  };

  return (
    <div className="flex items-start gap-2 pl-8 relative">
      <div className="flex items-center gap-2 z-10">
        <span className="text-sm text-gray-500 mt-2">#{optionIndex}</span>
        <GripVertical className="text-gray-400 mt-2 cursor-grab" size={20} />
      </div>

      <div
        className="flex-1 space-y-2 relative z-20"
        onDragStart={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Custom Report Header field */}
        <div className="flex items-center gap-2">
          <span className="w-32 text-sm text-gray-600">Report Header</span>
          <input
            type="text"
            value={option.custom_report_header || ""}
            onChange={(e) => handleCustomReportHeaderChange(e.target.value)}
            className="flex-1 p-2 border rounded-md bg-white"
            placeholder="Leave empty to use {question report header} - {option description} as report header"
            onDragStart={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </div>

        {Languages.map(
          (lang) =>
            (supported_languages || ["en", "tc"]).includes(lang.code) && (
              <div
                key={lang.code}
                className="flex items-center gap-2"
                onDragStart={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <span className="w-32 text-sm text-gray-600">{lang.name}</span>
                <input
                  type="text"
                  field="desc"
                  value={
                    option.desc[lang.code] ||
                    defaultOption.desc[lang.code] ||
                    ""
                  }
                  onChange={(e) => handleDescChange(lang.code, e.target.value)}
                  className="flex-1 p-2 border rounded-md bg-white"
                  placeholder={`Option in ${lang.name}`}
                  onDragStart={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
            )
        )}

        {/* Other Option checkbox */}
        {ENABLE_EXPERIMENTAL_FEATURE && (
          <div className="flex items-center gap-2 mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={option.other_option || false}
                onChange={handleOtherOptionChange}
                className="text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">
                Enable "Other" text input
              </span>
            </label>
          </div>
        )}

        {/* Other Option Placeholders (only shown when other_option is true) */}
        {option.other_option && (
          <div className="ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
            {Languages.map(
              (lang) =>
                supported_languages.includes(lang.code) && (
                  <div key={lang.code} className="flex items-center gap-2">
                    <span className="w-32 text-sm text-gray-600">
                      {lang.name} Placeholder
                    </span>
                    <input
                      type="text"
                      value={option.other_option_placeholder?.[lang.code] || ""}
                      onChange={(e) =>
                        handleOtherOptionPlaceholderChange(
                          lang.code,
                          e.target.value
                        )
                      }
                      className="flex-1 p-2 border rounded-md bg-white"
                      placeholder={`Placeholder in ${lang.name}`}
                    />
                  </div>
                )
            )}
          </div>
        )}

        {/* Mutex-related options - only show if mutex_id exists */}
        {question.mutex_id && (
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={option.exclude_mutex || false}
                  onChange={handleExcludeMutexChange}
                  className="text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">
                  Exclude from mutex validation
                </span>
              </label>

              {option.exclude_mutex && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={option.dominating_option || false}
                    onChange={(e) => {
                      const updates = { dominating_option: e.target.checked };
                      dispatch(
                        updateOption({ questionIndex, optionIndex, updates })
                      );
                    }}
                    className="text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    Dominating option
                  </span>
                </label>
              )}
            </div>

            {/* Help text to explain the behavior */}
            <div className="text-xs text-gray-500">
              {option.exclude_mutex
                ? option.dominating_option
                  ? "When this option is selected, it will be automatically selected in all following questions of the same mutex group."
                  : "This option can be selected multiple times across questions in the same mutex group."
                : "This option can only be selected once across all questions in the same mutex group."}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleRemove}
        className="p-2 text-gray-500 hover:text-red-500 relative z-20"
        onDragStart={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

export default OptionEditor;
